import {Location, LocationStrategy, PathLocationStrategy} from '@angular/common';
import {Component, HostListener, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {takeUntil} from 'rxjs/operators';
import {AuthService} from './auth/services/auth.service';
import {MonitoringService} from './monitoring/monitoring.service';
import {NavService} from './nav/services/nav.service';
import {UnsubscribeComponent} from './shared-components/components/unsubscribe/unsubscribe.abstract';
import {SessionStorageService} from './shared-services/services/session-storage.service';
import {SwUpdate} from '@angular/service-worker';
import {MixPanelService} from './shared-services/services/mixpanel.service';
import {GetUrlRequest} from "./auth/models/get-url-request.model";
import {ThemeService} from './shared-services/services/theme.service';
import {Theme} from "./models/themes/theme.model";
import {Observable} from "rxjs";
import { RawB2cToken } from './auth/models/raw-bwc-token.model';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [Location, {provide: LocationStrategy, useClass: PathLocationStrategy}],
    standalone: false
})
export class AppComponent extends UnsubscribeComponent implements OnInit {

    public loading = true;

    constructor(
        private authService: AuthService,
        private themeService: ThemeService,
        private location: Location,
        private mixPanelService: MixPanelService,
        private monitoringService: MonitoringService, // Don't remove, needed for monitoring
        private navService: NavService,
        private router: Router,
        private sessionService: SessionStorageService,
        private swUpdate: SwUpdate
    ) {
        super();
    }

    @HostListener('window:beforeunload')
    onBeforeUnload(): void {
        this.sessionService.removeItem('companyCode');
    }

    ngOnInit(): void {
        this.mixPanelService.setUpMixPanel(false);
        this.checkTimer();

        if (this.swUpdate.isEnabled) {
            this.swUpdate.versionUpdates
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((event) => {
                    if (event.type === "VERSION_READY") {
                        window.location.reload();
                    }
                });
        }
    }

    private checkTimer(): void {
        this.loading = !this.authService.isAuthenticated();
        const lastVisitedUrl = this.authService.getLastVisitedUrl();

        this.authService.tokenRefreshOnPageReloadEvent.asObservable()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(res => {
                this.loading = false;
            }, error => {
                this.loading = false;
            });

        const params = new URLSearchParams(window.location.search);
        const routeToken = params.get('token');
        const routeRefreshToken = params.get('refresh');
        const routeSource = params.get('source');

        const fragment = window.location.href.split('#id_token_hint')[1];

        const hasTokenInRoute = routeToken && routeRefreshToken;
        const hasTokenInFragment = !!fragment;

        this.navService.setVisibility(routeSource);

        if (routeToken && !hasTokenInRoute) {
            this.sessionService.setItem('widgetAccessToken', routeToken);
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete('token');
            cleanUrl.searchParams.delete('refresh');
            this.sessionService.setItem('widgetFullUrl', cleanUrl.toString());
            const urlMatch = window.location.search.match(/[?&]url=(.+?)(?:&token=|$)/);
            if (urlMatch) {
                this.sessionService.setItem('widgetUrl', urlMatch[1]);
            }
            const osVersion = params.get('OSVersion') ?? params.get('osVersion');
            if (osVersion) {
                this.sessionService.setItem('widgetOsVersion', osVersion);
            }

            this.authService.processWidgetB2cResponse(new RawB2cToken({ accessToken: routeToken }));
        }

        this.removeQueryParam('token');
        this.removeQueryParam('refresh');

        if (hasTokenInRoute) {
            this.completeB2cLogin(new RawB2cToken({
                accessToken: routeToken,
                refreshToken: routeRefreshToken
            }));
        } else if (hasTokenInFragment) {
            this.loading = !this.authService.isAuthenticated();

            const token = fragment.split('=')[1];

            this.removeFragmentFromURL();

            this.authService.hgSSO(token)
                .pipe(takeUntil(this.unsubscriber))
                .subscribe(() => {
                    if (this.loading) {
                        this.loadPage(lastVisitedUrl);
                    }
                }, _ => {
                    this.loadPage(lastVisitedUrl);
                    this.loadImmediately();
                });
        } else {
            this.loadImmediately();
        }
    }

    private completeB2cLogin(response: RawB2cToken): void {
        this.authService.processWidgetB2cResponse(response);

        const b2cUrls = this.authService.b2cUrls;

        if (!b2cUrls) {
            const themeObservable = !this.themeService.hasThemeInCache()
                ? this.themeService.loadThemeForHost()
                : new Observable(observer => {
                    observer.next(this.themeService.getTheme());
                    observer.complete();
                });

            themeObservable
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((theme: Theme) => {
                    this.authService.getB2CURLs(new GetUrlRequest({
                        companyCode: theme.companyCode
                    }))
                        .pipe(takeUntil(this.unsubscriber))
                        .subscribe(response => {
                            this.authService.b2cUrls = response;
                            this.authService.refreshAuthToken().subscribe();
                        });
                });
        } else {
            this.authService.refreshAuthToken().subscribe();
        }

        this.loading = false;
    }

    private removeQueryParam(param: string): void {
        const url = new URL(window.location.href);
        url.searchParams.delete(param);
        window.history.replaceState({}, '', url.toString());
    }

    private loadImmediately(): void {
        this.loading = false;
        const path = this.location.path(false);

        if (path.includes('code=')) {
            return;
        }

        this.authService.refreshTokenOnPageReload(path);
    }

    private loadPage(lastVisitedUrl: string): void {
        this.loading = false;
        this.router.navigateByUrl(lastVisitedUrl || '/dashboard');
    }

    private removeFragmentFromURL(): void {
        const url = new URL(window.location.href);
        url.hash = '';
        window.history.replaceState({}, '', url.toString());
    }
}
